import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ListTable from '../components/ListTable';
import Container from '../layouts/Container';
import { getAdminUsers, deleteUser } from '../services/project/userService';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button } from 'react-bootstrap';
import EditUserModal from '../components/EditUserModal';
import { useProjects } from '../context/ProjectContext';
import ResetPasswordModal from '../components/ResetPasswordModal';
import { Key, Pencil, XOctagon  } from 'react-bootstrap-icons';
import AnchorTag from '../components/AnchorTag';

function AdminUsers() {
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const { projects } = useProjects();

  useEffect(() => {
    fetchAdminUsers();
    setCurrentUser(JSON.parse(localStorage.getItem('user')));
  }, []);

  const fetchAdminUsers = async () => {
    setLoading(true);
    try {
      const response = await getAdminUsers();
      if (!response.success) {
        toast.error(response.data?.errorMessage || 'Error fetching users.');
        return;
      }
      setAdminUsers(response.adminUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

const handleEdit = (user) => {
  setShowModal(true);
  setSelectedUser(user);
}

const handleResetPassword = (user) => {
  setShowPasswordModal(true);
  setSelectedUser(user);
}

const handleDeleteUser = async (userId) => {
  const confirmed = window.confirm('Are you sure?');
  if (!confirmed) return;
  try {
    const response = await deleteUser(userId);
    if (!response.success) {
      toast.error(response.data?.errorMessage || 'Error deleting user.');
      return;
    }
    toast.success(response.message);
    fetchAdminUsers();
  } catch (error) {
    console.error('Error deleting user:', error);
    toast.error(error.response?.data?.errorMessage || 'Something went wrong');
  }
}

const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor(
      row => row.id,
      {
        id: 'id',
        header: 'ID',
        cell: info => info.getValue(),
      }
    ),
    columnHelper.accessor(
      row => row.username,
      {
        id: 'username',
        header: 'Username',
        cell: info => info.getValue(),
      }
    ),
    columnHelper.display({
      id: 'role',
      header: 'Role',
      cell: info => {
        const user = info.row.original;
        return (
          <>
            {user.roles.map((role) => (
              <Badge key={role.id} bg="primary" className="me-1">
                {role?.projectId !== 0 ? `${projects.find(p => p.id === role?.projectId)?.name}_` : ''}{role?.name}
              </Badge>
            ))}
          </>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: info => {
        const user = info.row.original;
        if (currentUser?.username === user.username) return null;
        return (
          <div className="d-flex gap-2">
            <AnchorTag onClick={() => handleEdit(user)} hoverText='Edit'>
              <Pencil />
            </AnchorTag>
            <AnchorTag onClick={() => handleResetPassword(user)} hoverText='Reset Password'>
              <Key />
            </AnchorTag>
            <AnchorTag onClick={() => handleDeleteUser(user.id)} hoverText='Delete'>
              <XOctagon />
            </AnchorTag>
          </div>
        );
      },
    }),
  ];



  return (
    <Container>
      
      <div className="d-flex align-items-center mb-3 gap-2">
        <h2>Admin Users</h2>
        <Button onClick={() => {
          setSelectedUser(null);
          setShowModal(true);
        }}>
          Add New
        </Button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <ListTable data={adminUsers} columns={columns} />
          {showModal && (
            <EditUserModal
              projects={projects}
              user={selectedUser}
              show={showModal}
              refetch={fetchAdminUsers}
              onClose={() => setShowModal(false)}
            />
          )}
          {showPasswordModal && (
            <ResetPasswordModal
              user={selectedUser}
              show={showPasswordModal}
              refetch={fetchAdminUsers}
              onClose={() => setShowPasswordModal(false)}
            />
          )}
        </>
      )}
    </Container>
  );
}

export default AdminUsers;
